import { simpleApi } from "~/common/API/simpleApi";
import type {
  BlogArticle,
  BlogArticleStatus,
  BlogAuthorPayload,
  BlogMediaUploadResult,
  BlogTopicPayload,
} from "~/interfaces/blog";

// Админские ручки блога живут под /admin/blog и требуют Auth-Type: asAdmin.
const asAdmin = { authType: "asAdmin" } as const;

export interface BlogArticlesFilter {
  search?: string;
  topicId?: number;
  tagId?: number;
  status?: BlogArticleStatus[];
  authorId?: number;
  createdFrom?: string;
  createdTo?: string;
  publishedFrom?: string;
  publishedTo?: string;
  pageNumber?: number;
  perPage?: number;
  sortFields?: string[];
  sortDirections?: string[];
}

// Фильтр публичного списка статей (/blog/articles — без авторизации).
export interface BlogPublicArticlesFilter {
  search?: string;
  // Область поиска из макета: по названию или по содержимому
  searchIn?: string;
  topicSlug?: string;
  tagSlugs?: string | string[];
  authorSlug?: string;
  sortFields?: string[];
  sortDirections?: string[];
  pageNumber?: number;
  perPage?: number;
}

export default class BlogService {
  // ===== Статьи =====

  static getArticles = async (filter: BlogArticlesFilter = {}) => {
    const params: Record<string, any> = {};
    if (filter.search) params.search = filter.search;
    if (filter.topicId) params.topicId = filter.topicId;
    if (filter.tagId) params.tagId = filter.tagId;
    if (filter.authorId) params.authorId = filter.authorId;
    // Массивы бэк ждёт в формате status[]=draft&status[]=published
    if (filter.status?.length) params["status[]"] = filter.status;
    if (filter.sortFields?.length) params["sortFields[]"] = filter.sortFields;
    if (filter.sortDirections?.length) params["sortDirections[]"] = filter.sortDirections;
    if (filter.createdFrom) params.createdFrom = filter.createdFrom;
    if (filter.createdTo) params.createdTo = filter.createdTo;
    if (filter.publishedFrom) params.publishedFrom = filter.publishedFrom;
    if (filter.publishedTo) params.publishedTo = filter.publishedTo;
    params.pageNumber = filter.pageNumber ?? 1;
    params.perPage = filter.perPage ?? 20;

    try {
      return await simpleApi("/admin/blog/articles", { ...asAdmin, params });
    } catch (err) {
      console.error("BlogService.getArticles", err);
      return null;
    }
  };

  static getArticle = async (id: number) => {
    try {
      return await simpleApi("/admin/blog/article", { ...asAdmin, params: { id } });
    } catch (err) {
      console.error("BlogService.getArticle", err);
      return null;
    }
  };

  static createArticle = async (body: BlogArticle) => {
    return await simpleApi("/admin/blog/article", { ...asAdmin, method: "POST", body });
  };

  // PUT требует полный объект статьи, а не частичный набор полей.
  static updateArticle = async (body: BlogArticle & { id: number }) => {
    return await simpleApi("/admin/blog/article", { ...asAdmin, method: "PUT", body });
  };

  static deleteArticle = async (id: number) => {
    return await simpleApi("/admin/blog/article", { ...asAdmin, method: "DELETE", params: { id } });
  };

  // publishedAt обязателен только для статуса scheduled (и должен быть в будущем).
  static setArticleStatus = async (
    id: number,
    status: BlogArticleStatus,
    publishedAt?: string
  ) => {
    return await simpleApi("/admin/blog/article/status", {
      ...asAdmin,
      method: "PUT",
      body: publishedAt ? { id, status, publishedAt } : { id, status },
    });
  };

  // Токен превью живёт час и привязан к конкретной статье.
  static createPreviewToken = async (id: number) => {
    return await simpleApi("/admin/blog/article/preview-token", {
      ...asAdmin,
      method: "POST",
      body: { id },
    });
  };

  // ===== Публичное чтение =====
  // Публичные ручки блога авторизации не требуют, поэтому идут мимо simpleApi
  // (он читает localStorage и шлёт Auth-Type — на SSR это бы упало).
  static publicFetch = async <T = any>(endpoint: string, params?: Record<string, any>) => {
    const config = useRuntimeConfig();
    return (await $fetch(endpoint, {
      baseURL: config.public.LIGHT_API_ORIGIN as string,
      params,
    })) as T;
  };

  static getArticlePreview = async (token: string) =>
    BlogService.publicFetch("/blog/article/preview", { token });

  // Слаг бэк принимает query-параметром: его роутер не пропускает дефис
  // в сегменте пути. Красивые адреса собирает фронт.
  static getPublicArticle = async (slug: string) =>
    BlogService.publicFetch("/blog/article", { slug });

  static getPublicArticles = async (filter: BlogPublicArticlesFilter = {}) => {
    const params: Record<string, any> = {};
    if (filter.search) params.search = filter.search;
    if (filter.search && filter.searchIn) params.searchIn = filter.searchIn;
    if (filter.topicSlug) params.topicSlug = filter.topicSlug;
    if (filter.authorSlug) params.authorSlug = filter.authorSlug;
    // tagSlugs — именно во множественном числе: на tagSlug[] бэк отдаёт пустой
    // ответ. Массив нужен, чтобы «Похожие» собирались сразу по всем тегам статьи.
    const tagSlugs = ([] as string[]).concat(filter.tagSlugs ?? []).filter(Boolean);
    if (tagSlugs.length) params["tagSlugs[]"] = tagSlugs;
    if (filter.sortFields?.length) params["sortFields[]"] = filter.sortFields;
    if (filter.sortDirections?.length) params["sortDirections[]"] = filter.sortDirections;
    params.pageNumber = filter.pageNumber ?? 1;
    params.perPage = filter.perPage ?? 12;
    return BlogService.publicFetch("/blog/articles", params);
  };

  // Популярное — та же ручка списка, отсортированная по просмотрам.
  static getPopularArticles = async (perPage = 5) =>
    BlogService.getPublicArticles({
      sortFields: ["views"],
      sortDirections: ["desc"],
      perPage,
    });

  static getPublicTopic = async (slug: string) =>
    BlogService.publicFetch("/blog/topic", { slug });

  static getPublicTopics = async () => BlogService.publicFetch("/blog/topics");

  // search ищет по названию тега
  static getPublicTags = async (search?: string) =>
    BlogService.publicFetch("/blog/tags", search ? { search } : undefined);

  // Просмотр засчитываем только с клиента: на SSR считались бы краулеры,
  // а при кэшировании счётчик перестал бы расти вовсе.
  static countView = async (id: number) => {
    const config = useRuntimeConfig();
    return await $fetch("/blog/article/view", {
      baseURL: config.public.LIGHT_API_ORIGIN as string,
      method: "POST",
      body: { id },
    });
  };

  // ===== Справочники для формы статьи =====

  static getTopics = async () => {
    try {
      return await simpleApi("/admin/blog/topics", { ...asAdmin });
    } catch (err) {
      console.error("BlogService.getTopics", err);
      return null;
    }
  };

  static getTopic = async (id: number) => {
    return await simpleApi("/admin/blog/topic", { ...asAdmin, params: { id } });
  };

  static createTopic = async (body: BlogTopicPayload) => {
    return await simpleApi("/admin/blog/topic", { ...asAdmin, method: "POST", body });
  };

  // PUT требует id и name, поэтому отправляем объект целиком, а не патчем.
  static updateTopic = async (body: BlogTopicPayload & { id: number }) => {
    return await simpleApi("/admin/blog/topic", { ...asAdmin, method: "PUT", body });
  };

  static deleteTopic = async (id: number) => {
    return await simpleApi("/admin/blog/topic", { ...asAdmin, method: "DELETE", params: { id } });
  };

  static getAuthors = async (search?: string) => {
    try {
      return await simpleApi("/admin/blog/authors", {
        ...asAdmin,
        params: search ? { search } : undefined,
      });
    } catch (err) {
      console.error("BlogService.getAuthors", err);
      return null;
    }
  };

  static getAuthor = async (id: number) => {
    return await simpleApi("/admin/blog/author", { ...asAdmin, params: { id } });
  };

  static createAuthor = async (body: BlogAuthorPayload) => {
    return await simpleApi("/admin/blog/author", { ...asAdmin, method: "POST", body });
  };

  // PUT требует id и name, поэтому отправляем объект целиком, а не патчем.
  static updateAuthor = async (body: BlogAuthorPayload & { id: number }) => {
    return await simpleApi("/admin/blog/author", { ...asAdmin, method: "PUT", body });
  };

  static deleteAuthor = async (id: number) => {
    return await simpleApi("/admin/blog/author", { ...asAdmin, method: "DELETE", params: { id } });
  };

  static createTag = async (name: string, slug?: string) => {
    return await simpleApi("/admin/blog/tag", {
      ...asAdmin,
      method: "POST",
      body: slug ? { name, slug } : { name },
    });
  };

  // Отдельной ручки на один тег у бэка нет — данные для формы берём из списка.
  static updateTag = async (body: { id: number; name: string; slug?: string }) => {
    return await simpleApi("/admin/blog/tag", { ...asAdmin, method: "PUT", body });
  };

  static deleteTag = async (id: number) => {
    return await simpleApi("/admin/blog/tag", { ...asAdmin, method: "DELETE", params: { id } });
  };

  // ===== Медиа =====
  // multipart/form-data, поле file. SVG бэк отклоняет.
  // Лимиты: картинки 15 МБ, видео 200 МБ, документы 25 МБ.
  static uploadMedia = async (file: File, kind?: string): Promise<BlogMediaUploadResult> => {
    const data = new FormData();
    data.append("file", file);
    if (kind) data.append("kind", kind);
    return (await simpleApi("/admin/blog/media/upload", {
      ...asAdmin,
      method: "POST",
      body: data,
    })) as BlogMediaUploadResult;
  };
}
